import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AUTH_TOKEN } from "constants/AuthConstant";
import {
  getCurrentUser,
  loginUser,
  logoutUser,
  openRegisterUser,
  registerUser,
  sendOtp,
  ssoExchange,
} from "../../api/auth";
import { getCookie, removeCookie, setCookie } from "../../utils/cookieHandler";
import {
  getClientRegisterUrl,
  getStoredAttribution,
  takeStoredClientRegisterUrl,
} from "../../utils/attribution";
import { getRedirectParam, takePostLoginRedirect } from "../../utils/authRedirect";
import {
  flushAndRedirect,
  gaTrack,
  googleAdsTrack,
  linkedinTrack,
  metaTrack,
  metaTrackCustom,
  redditTrack,
  redditIdentify,
} from "../../utils/tracking";

const getRedirectUrl = () => getRedirectParam() || "/";

// The OAuth round-trip lands on /auth/{provider}/callback, which carries no
// ?redirect= — the destination was stashed before the hop. Google One Tap never
// leaves the page, so its ?redirect= is still on the URL and wins.
const getSSORedirectUrl = () =>
  getRedirectParam() || takePostLoginRedirect() || "/";

// If arriving with ?auth_token=..., save it to cookie before initialState reads it.
// This handles login-from-public-site flow (redirect with token in URL).
(function extractUrlAuthToken() {
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get("auth_token");
  if (urlToken) {
    setCookie(AUTH_TOKEN, urlToken);
    params.delete("auth_token");
    const cleanUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);
  }
})();

export const initialState = {
  user: null,
  loading: false,
  message: "",
  showMessage: false,
  redirect: "",
  token: getCookie(AUTH_TOKEN) || null,
  shouldValidateOTP: false,
};

export const getUser = createAsyncThunk(
  "auth/,me",
  async (data, { rejectWithValue }) => {
    try {
      const response = await getCurrentUser();
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

export const signIn = createAsyncThunk(
  "auth/login",
  async (data, { rejectWithValue }) => {
    try {
      let response;
      if (data?.otp_code) {
        response = await sendOtp(data);
      } else {
        response = await loginUser(data);
      }

      const payload = {};
      if (response.token) {
        payload.token = response.token;
        setCookie(AUTH_TOKEN, response.token);
        metaTrackCustom("Login", { content_name: "login" });
        gaTrack("login", { method: "email" });
        redditIdentify({ email: data?.email });
        redditTrack("Custom", { customEventName: "Login" });
        flushAndRedirect(getRedirectUrl());
      }

      if (response.message) {
        payload.message = response.message;
      }
      return payload;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

export const signUp = createAsyncThunk(
  "auth/register",
  async (data, { rejectWithValue }) => {
    try {
      const attribution = getStoredAttribution();
      const clientRegisterUrl = getClientRegisterUrl();
      const payload = {
        ...data,
        ...(attribution ? { attribution } : {}),
        ...(clientRegisterUrl ? { client_register_url: clientRegisterUrl } : {}),
      };
      const response = await registerUser(payload);
      const token = response.token;
      setCookie(AUTH_TOKEN, token);
      const dedupId = response.user?.id ?? (token ? token.slice(-12) : Date.now());
      metaTrack("CompleteRegistration", {
        content_name: "signup",
        em: data?.email ? String(data.email).toLowerCase().trim() : undefined,
      });
      linkedinTrack("SignUp");
      googleAdsTrack("SignUp", { transactionId: `signup_${dedupId}` });
      gaTrack("sign_up", { method: "email", transaction_id: `signup_${dedupId}` });
      redditIdentify({ email: data?.email, externalId: dedupId });
      redditTrack("SignUp", { transactionId: `signup_${dedupId}` });
      // Redirect will be handled by React Router based on is_onboarding_complete
      flushAndRedirect(getRedirectUrl());
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

export const signUpOpen = createAsyncThunk(
  "auth/open-register",
  async (data, { rejectWithValue }) => {
    try {
      const attribution = getStoredAttribution();
      const clientRegisterUrl = getClientRegisterUrl();
      const payload = {
        ...data,
        ...(attribution ? { attribution } : {}),
        ...(clientRegisterUrl ? { client_register_url: clientRegisterUrl } : {}),
      };
      const response = await openRegisterUser(payload);
      const token = response.token;
      setCookie(AUTH_TOKEN, token);
      const dedupId = response.user?.id ?? (token ? token.slice(-12) : Date.now());
      metaTrack("CompleteRegistration", {
        content_name: "signup_open",
        em: data?.email ? String(data.email).toLowerCase().trim() : undefined,
      });
      linkedinTrack("SignUp");
      googleAdsTrack("SignUp", { transactionId: `signup_${dedupId}` });
      gaTrack("sign_up", { method: "open_register", transaction_id: `signup_${dedupId}` });
      redditIdentify({ email: data?.email, externalId: dedupId });
      redditTrack("SignUp", { transactionId: `signup_${dedupId}` });
      flushAndRedirect(getRedirectUrl());
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  },
);

export const signOut = createAsyncThunk("auth/logout", async (_, { rejectWithValue }) => {
  try {
    await logoutUser();
    return true;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || "Error");
  }
});

export const signInWithSSO = createAsyncThunk(
  "auth/signInWithSSO",
  async ({ provider, code, credential, redirect_uri, source }, { rejectWithValue }) => {
    try {
      const data = {
        has_agreed_to_privacy_policy: true,
        source: source || `sso-${provider}`,
      };
      if (credential) {
        data.credential = credential;
      } else {
        data.code = code;
        data.redirect_uri = redirect_uri;
      }
      const attribution = getStoredAttribution();
      if (attribution) {
        data.attribution = attribution;
      }
      // Consumed here rather than read from window: the callback URL is not where
      // the user signed up, and it carries the OAuth code.
      const clientRegisterUrl = takeStoredClientRegisterUrl();
      if (clientRegisterUrl) {
        data.client_register_url = clientRegisterUrl;
      }
      const response = await ssoExchange(provider, data);
      const token = response.token;
      setCookie(AUTH_TOKEN, token);
      // SSO is the same endpoint for new and returning users — fire Login only
      // (no Google Ads SignUp conversion) to avoid double-counting; SignUp
      // fires from the dedicated register page only.
      metaTrackCustom("Login", { content_name: `sso_${provider}` });
      gaTrack("login", { method: `sso_${provider}` });
      redditTrack("Custom", { customEventName: "Login" });
      // Fire LinkedIn + Reddit SignUp conversion only when SSO was initiated from
      // the register page — login-page SSO is a returning user, not a new account.
      if (typeof source === "string" && source.startsWith("register-")) {
        linkedinTrack("SignUp");
        redditTrack("SignUp");
      }
      flushAndRedirect(getSSORedirectUrl());
      return token;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Authentication failed. Please try again.");
    }
  },
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    authenticated: (state, action) => {
      state.loading = false;
      state.redirect = "/";
      state.token = action.payload;
    },
    showAuthMessage: (state, action) => {
      state.message = action.payload;
      state.showMessage = true;
      state.loading = false;
    },
    hideAuthMessage: (state) => {
      state.message = "";
      state.showMessage = false;
    },
    signOutSuccess: (state) => {
      state.loading = false;
      state.token = null;
      state.redirect = "/";
    },
    showLoading: (state) => {
      state.loading = true;
    },
    signInSuccess: (state, action) => {
      state.loading = false;
      state.token = action.payload;
    },
    setUserLocale: (state, action) => {
      if (state.user) {
        state.user.locale = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUser.pending, (state) => {
        state.loading = true;
        state.shouldValidateOTP = false;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.shouldValidateOTP = false;
      })
      .addCase(getUser.rejected, (state, action) => {
        state.message = action.payload;
        state.loading = false;
        state.shouldValidateOTP = false;
      })
      .addCase(signIn.pending, (state) => {
        state.loading = true;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.loading = false;
        state.redirect = "/";
        if (action.payload?.token) {
          state.token = action.payload.token;
          state.shouldValidateOTP = false;
        }
        if (action.payload?.message) {
          state.shouldValidateOTP = true;
        }
      })
      .addCase(signIn.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.loading = false;
      })
      .addCase(signOut.fulfilled, (state) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        state.redirect = "/";
        state.shouldValidateOTP = false;
        removeCookie(AUTH_TOKEN);
      })
      .addCase(signOut.rejected, (state) => {
        state.loading = false;
        state.shouldValidateOTP = false;
      })
      .addCase(signUp.pending, (state) => {
        state.loading = true;
        state.shouldValidateOTP = false;
      })
      .addCase(signUp.fulfilled, (state, action) => {
        state.loading = false;
        state.shouldValidateOTP = false;
        state.redirect = "/";
        state.token = action.payload;
        state.message = null;
        state.showMessage = false;
      })
      .addCase(signUp.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.shouldValidateOTP = false;
        state.loading = false;
      })
      .addCase(signUpOpen.pending, (state) => {
        state.loading = true;
        state.shouldValidateOTP = false;
      })
      .addCase(signUpOpen.fulfilled, (state, action) => {
        state.loading = false;
        state.shouldValidateOTP = false;
        state.redirect = "/";
        state.token = action.payload;
        state.message = null;
        state.showMessage = false;
      })
      .addCase(signUpOpen.rejected, (state, action) => {
        state.message = action.payload;
        state.showMessage = true;
        state.shouldValidateOTP = false;
        state.loading = false;
      })
      .addCase(signInWithSSO.pending, (state) => {
        state.loading = true;
        state.shouldValidateOTP = false;
      })
      .addCase(signInWithSSO.fulfilled, (state, action) => {
        state.loading = false;
        state.shouldValidateOTP = false;
        state.redirect = "/";
        state.token = action.payload;
      })
      .addCase(signInWithSSO.rejected, (state, action) => {
        state.message = action.payload;
        state.shouldValidateOTP = false;
        state.showMessage = true;
        state.loading = false;
      });
  },
});

export const {
  authenticated,
  showAuthMessage,
  hideAuthMessage,
  signOutSuccess,
  showLoading,
  signInSuccess,
  setUserLocale,
} = authSlice.actions;

export default authSlice.reducer;
